import { useCallback, useEffect, useMemo, useState } from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import { IoClose } from "react-icons/io5";
import MainLayout from "@/Component/MainLayout";
import Breadcrumbs from "@/Component/Common/Breadcrumbs";
import useIsDesktop from "@/hooks/useIsDesktop";
import { fetchCart, removeCart } from "@/services/api/common";
import { getCartRequestParams, getRemoveCartParams } from "@/utils/cartAuth";
import {
  canBookCartItem,
  getCartBookingPath,
  getCartDetailPath,
  getCartModuleCookieValue,
  isHotelItem,
  normalizeCartResponse,
} from "@/utils/cartHelpers";
import { encryptNumberAES } from "@/utils/Encryption";
import { formatIndianPrice } from "@/utils/formatIndianPrice";
import { getCurrencySymbol } from "@/utils/getCurrencySymbol";
import { setCookie, withRegionPath } from "@/utils/session";
import { RiDeleteBin6Fill } from "react-icons/ri";
import styles from "./Cart.module.css";

function DetailRow({ label, value }) {
  if (!value) return null;

  return (
    <p className={styles.detailRow}>
      <span className={styles.detailLabel}>{label}:</span> {value}
    </p>
  );
}

function SaveBadge({ amount }) {
  if (!amount) return null;

  return (
    <span className={styles.saveBadge}>
      Save &nbsp;{getCurrencySymbol("green", 8)} {formatIndianPrice(amount)}
    </span>
  );
}

function CartItemCard({
  item,
  selected,
  onSelect,
  onTitleClick,
  onRemoveRequest,
  layout = "desktop",
}) {
  const isHotel = isHotelItem(item);

  return (
    <article
      className={`${styles.card} ${layout === "mobile" ? styles.cardMobile : ""} ${selected ? styles.cardSelected : ""
        }`}
      onClick={() => onSelect(item.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(item.id);
        }
      }}
    >
      <div className={styles.cardMain}>
        <div className={styles.imageWrap}>
          <div className={styles.imageThumb}>
            {item.image ? (
              <img src={item.image} alt="" className={styles.image} loading="lazy" />
            ) : (
              <div className={styles.imagePlaceholder} aria-hidden="true" />
            )}

            <label
              className={styles.selectWrap}
              onClick={(event) => event.stopPropagation()}
            >
              <input
                type="radio"
                name="cartItem"
                checked={selected}
                onChange={() => onSelect(item.id)}
                className={styles.radioInput}
                aria-label={`Select ${item.title || "cart item"}`}
              />
            </label>
          </div>

          <div className={styles.mobileTitleWrap}>
            <button
              type="button"
              className={`${styles.cardTitle2} ${styles.textClamp2}`}
              onClick={(event) => {
                event.stopPropagation();
                onTitleClick(item);
              }}
            >
              {item.title || "Cart Item"}
            </button>
            {item.subtitle ? (
              <p className={`${styles.cardSubtitle2} ${styles.textClamp2}`}>{item.subtitle}</p>
            ) : null}
          </div>
        </div>

        <div className={styles.cardBody}>
          <div className={styles.cardTop}>
            <div className={styles.cardCopy}>
              <button
                type="button"
                className={`${styles.cardTitle} ${styles.textClamp2}`}
                onClick={(event) => {
                  event.stopPropagation();
                  onTitleClick(item);
                }}
              >
                {item.title || "Cart Item"}
              </button>
              {item.subtitle ? (
                <p className={`${styles.cardSubtitle} ${styles.textClamp2}`}>{item.subtitle}</p>
              ) : null}
            </div>
          </div>

          <div className={styles.details}>
            {isHotel ? (
              <>
                <DetailRow label="Check-in" value={item.checkIn} />
                <DetailRow label="Check-out" value={item.checkOut} />
                <DetailRow label="Nights" value={item.nights} />
                <DetailRow label="Guests & Rooms" value={item.guestsRooms || item.travelers} />
              </>
            ) : (
              <>
                <DetailRow label="Duration" value={item.duration} />
                <DetailRow label="Inclusion" value={item.inclusion} />
                <DetailRow label="Travel Dates" value={item.travelDates} />
                <DetailRow label="Travelers" value={item.travelers} />
              </>
            )}
          </div>
        </div>
      </div>

      <div className={styles.cardPricing}>
        <button
          type="button"
          className={styles.removeBtn}
          aria-label="Remove item"
          onClick={(event) => {
            event.stopPropagation();
            onRemoveRequest(item);
          }}
        >
          {layout === "mobile" ? <IoClose size={22} /> : <RiDeleteBin6Fill size={16} />}
        </button>

        <div className={styles.totalRow}>
          {item.unitPrice && item.quantity > 1 ? (
            <p className={styles.unitPrice}>
              {getCurrencySymbol("main", 20)} {formatIndianPrice(item.unitPrice)} x{" "}
              {item.quantity}
            </p>
          ) : null}
          <SaveBadge amount={item.saveAmount} />
          <p className={styles.totalPrice}>
            {getCurrencySymbol("main", 20)}{formatIndianPrice(item.totalPrice)}
          </p>
        </div>
      </div>
    </article>
  );
}

function OrderSummary({ travelerCount, grandTotal, saveAmount, onBookNow, booking }) {
  return (
    <aside className={styles.summaryCard}>
      <p className={styles.summaryTitle}>Grand Total ({travelerCount})</p>
      <div className={styles.summaryTotalDiv}>
        <p className={styles.summaryTotal}>
          {getCurrencySymbol("main", 26)} {formatIndianPrice(grandTotal)}
        </p>
        <SaveBadge amount={saveAmount} />
      </div>
      <button
        type="button"
        className={styles.bookBtn}
        onClick={onBookNow}
        disabled={booking}
      >
        {booking ? "Processing..." : "Book Now"}
      </button>
    </aside>
  );
}

function RemoveModal({ item, open, onClose, onConfirm, removing }) {
  if (!open || !item) return null;

  return (
    <div className={styles.removeModalRoot} role="dialog" aria-modal="true">
      <button
        type="button"
        className={styles.removeOverlay}
        aria-label="Close"
        onClick={onClose}
        disabled={removing}
      />
      <div className={styles.removeSheet}>
        <div className={styles.removeHeader}>
          <h3>Remove from Cart</h3>
          <button
            type="button"
            className={styles.removeClose}
            aria-label="Close"
            onClick={onClose}
            disabled={removing}
          >
            <IoClose size={22} />
          </button>
        </div>
        <div className={styles.removePreview}>
          <div className={styles.removeImageWrap}>
            {item.image ? <img src={item.image} alt="" /> : null}
          </div>
          <div>
            <p className={styles.removeTitle}>{item.title}</p>
            {item.subtitle ? <p className={styles.removeSubtitle}>{item.subtitle}</p> : null}
          </div>
        </div>
        <div className={styles.removeConfirmBtnDiv}>
          <button
            type="button"
            className={styles.removeConfirmBtn}
            onClick={onConfirm}
            disabled={removing}
          >
            {removing ? "Removing..." : "Remove"}
          </button>
        </div>
      </div>
    </div>
  );
}

function getTravelerCount(item) {
  if (!item) return 0;
  const raw = item.raw || {};
  const explicit = Number(raw.totalTravelers || 0);
  if (explicit > 0) return explicit;

  const adults = Number(raw.adult ?? raw.adults ?? 0);
  const children = Number(raw.child ?? raw.children ?? 0);
  const infants = Number(raw.infant ?? raw.infants ?? 0);
  return adults + children + infants || item.quantity || 1;
}

export default function Cart() {
  const router = useRouter();
  const { isDesktop, mounted } = useIsDesktop(900);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [message, setMessage] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [booking, setBooking] = useState(false);

  const loadCart = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    return fetchCart(getCartRequestParams())
      .then((response) => {
        const normalized = normalizeCartResponse(response);
        setItems(normalized.items);
        setMessage(normalized.message);
        setSelectedId((current) => {
          if (!normalized.items.length) return null;
          if (current && normalized.items.some((item) => item.id === current)) {
            return current;
          }
          return normalized.items[0].id;
        });
      })
      .catch((error) => {
        console.error("Failed to load cart:", error);
        setItems([]);
        setMessage("Unable to load your cart right now.");
        setSelectedId(null);
      })
      .finally(() => {
        if (!silent) setLoading(false);
      });
  }, []);

  useEffect(() => {
    loadCart();
  }, [loadCart]);

  const selectedItem = useMemo(
    () => items.find((item) => item.id === selectedId) || null,
    [items, selectedId]
  );

  const grandTotal = selectedItem?.totalPrice || 0;
  const saveAmount = selectedItem?.saveAmount || 0;
  const travelerCount = getTravelerCount(selectedItem);

  const handleTitleClick = (item) => {
    const detailPath = getCartDetailPath(item);
    if (!detailPath) return;
    router.push(withRegionPath(detailPath));
  };

  const handleBookNow = () => {
    if (!selectedItem || booking) return;

    if (!canBookCartItem(selectedItem)) {
      window.alert("Online booking is not available for this cart item yet.");
      return;
    }

    setBooking(true);

    const encryptedCoupon = selectedItem.couponCode
      ? encryptNumberAES(selectedItem.couponCode)
      : "";
    const bookingPath = getCartBookingPath(selectedItem, encryptedCoupon);

    setCookie("CartId", String(selectedItem.id));
    setCookie("id", "");
    setCookie("module", getCartModuleCookieValue(selectedItem));

    router.push(withRegionPath(bookingPath));
  };

  const handleRemoveConfirm = async () => {
    if (!removeTarget || removing) return;

    setRemoving(true);
    try {
      const response = await removeCart(getRemoveCartParams(removeTarget.id));
      if (String(response?.status || "").toLowerCase() !== "success") {
        window.alert(response?.message || "Failed to remove item from cart.");
        return;
      }
      setRemoveTarget(null);
      await loadCart(true);
    } catch (error) {
      window.alert(
        error?.response?.data?.message ||
        "Something went wrong while removing this item."
      );
    } finally {
      setRemoving(false);
    }
  };

  const handleDesktopRemove = async (item) => {
    if (removing) return;

    setRemoving(true);
    try {
      const response = await removeCart(getRemoveCartParams(item.id));
      if (String(response?.status || "").toLowerCase() !== "success") {
        window.alert(response?.message || "Failed to remove item from cart.");
        return;
      }
      await loadCart(true);
    } catch (error) {
      window.alert(
        error?.response?.data?.message ||
        "Something went wrong while removing this item."
      );
    } finally {
      setRemoving(false);
    }
  };

  const handleRemoveRequest = (item) => {
    if (mounted && isDesktop) {
      handleDesktopRemove(item);
      return;
    }
    setRemoveTarget(item);
  };

  const isEmpty = !loading && items.length === 0;
  const layout = mounted && isDesktop ? "desktop" : "mobile";

  if (loading) {
    return (
      <MainLayout hideHeader>
        <div className={styles.loadingWrap}>
          <Image src="/assets/loading.gif" alt="Loading cart" width={100} height={100} />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout hideHeader>
      <Breadcrumbs />
      <div className={`standard-padding ${styles.page}`}>
        {isEmpty ? (
          <div className={styles.emptyState}>
            <Image
              src="/assets/icons/Cartempty.svg"
              alt=""
              width={150}
              height={150}
              className={styles.emptyImage}
            />
            <h1 className="standard-heading">Oops! No trips in your cart yet.</h1>
            <p className="standard-subheading">
              {message || "Start exploring and find something you love!"}
            </p>
            <button
              type="button"
              className={styles.homeButton}
              onClick={() => router.push(withRegionPath("/holiday"))}
            >
              Go to Homepage
            </button>
          </div>
        ) : (
          <div className={styles.layout}>
            <div className={styles.itemsColumn}>
              {items.map((item) => (
                <CartItemCard
                  key={item.id}
                  item={item}
                  selected={selectedId === item.id}
                  onSelect={setSelectedId}
                  onTitleClick={handleTitleClick}
                  onRemoveRequest={handleRemoveRequest}
                  layout={layout}
                />
              ))}
            </div>

            {mounted && isDesktop ? (
              <OrderSummary
                travelerCount={travelerCount}
                grandTotal={grandTotal}
                saveAmount={saveAmount}
                onBookNow={handleBookNow}
                booking={booking}
              />
            ) : null}
          </div>
        )}
      </div>

      {mounted && !isDesktop && !isEmpty ? (
        <div className={styles.mobileFooter}>
          <div className={styles.mobileFooterCopy}>
            <p className={styles.mobileFooterLabel}>Grand Total ({travelerCount})</p>
            <div className={styles.mobileFooterTotalRow}>
              <p className={styles.mobileFooterTotal}>
                {getCurrencySymbol("main", 18)} {formatIndianPrice(grandTotal)}
              </p>
              <SaveBadge amount={saveAmount} />
            </div>
          </div>
          <button
            type="button"
            className={styles.bookBtn}
            onClick={handleBookNow}
            disabled={booking || !selectedItem}
          >
            {booking ? "Processing..." : "Book Now"}
          </button>
        </div>
      ) : null}

      <RemoveModal
        item={removeTarget}
        open={Boolean(removeTarget)}
        onClose={() => setRemoveTarget(null)}
        onConfirm={handleRemoveConfirm}
        removing={removing}
      />
    </MainLayout>
  );
}
